import React, { useState } from 'react';
import {
  Container,
  Accordion,
  Group,
  Text,
  TextInput,
  Select,
  NumberInput,
  Badge,
  Grid,
  Paper,
  ThemeIcon,
  Title,
  Stack,
  ActionIcon,
  Box
} from '@mantine/core';
import {
  IconChevronDown,
  IconCurrencyDollar,
  IconPercentage,
  IconHash,
  IconEdit,
  IconCheck,
  IconX,
  IconCalendar,
  IconTrendingUp,
  IconTrendingDown,
  IconTargetArrow
} from '@tabler/icons-react';

const GoalManagement = () => {
  const [editingId, setEditingId] = useState(null);
  const [metrics, setMetrics] = useState({
    repairs: [
      // Planned Repairs Spend
      {
        id: 1,
        name: 'Planned Repairs Spend - Goal Margin %',
        type: 'percent',
        value: 23.4,
        frequency: 'monthly',
        direction: 'higher'
      },
      // Landed Repairs Spend
      {
        id: 2,
        name: 'Landed Repairs Spend - Annual Goal',
        type: 'dollar',
        value: 10950000,
        frequency: 'annual',
        direction: 'lower'
      },
      {
        id: 3,
        name: 'Landed Repairs Spend - Margin %',
        type: 'percent',
        value: 23.4,
        frequency: 'monthly',
        direction: 'higher'
      },
      {
        id: 4,
        name: 'Landed Repairs Spend - Margin $',
        type: 'dollar',
        value: 300000,
        frequency: 'monthly',
        direction: 'higher'
      },
      // Revenue/Margin
      {
        id: 5,
        name: 'Revenue/Margin - Monthly Goal Revenue',
        type: 'dollar',
        value: 1100000,
        frequency: 'monthly',
        direction: 'higher'
      },
      {
        id: 6,
        name: 'Revenue/Margin - Margin %',
        type: 'percent',
        value: 23.4,
        frequency: 'monthly',
        direction: 'higher'
      },
      {
        id: 7,
        name: 'Revenue/Margin - Margin $',
        type: 'dollar',
        value: 300000,
        frequency: 'monthly',
        direction: 'higher'
      },
      // Inventory Health
      {
        id: 8,
        name: 'Inventory Health - 91-180 Days',
        type: 'percent',
        value: 26,
        frequency: 'monthly',
        direction: 'lower'
      },
      {
        id: 9,
        name: 'Inventory Health - 181-365 Days',
        type: 'percent',
        value: 16,
        frequency: 'monthly',
        direction: 'lower'
      },
      {
        id: 10,
        name: 'Inventory Health - 365+ Days',
        type: 'percent',
        value: 2,
        frequency: 'monthly',
        direction: 'lower'
      }
    ],
    repairsIndividual: [
      // Planned Repairs Spend
      {
        id: 11,
        name: 'Planned Repairs Spend - Goal Margin %',
        type: 'percent',
        value: 23.4,
        frequency: 'monthly',
        direction: 'higher'
      },
      // Landed Repairs Spend
      {
        id: 12,
        name: 'Landed Repairs Spend - Annual Goal',
        type: 'dollar',
        value: 5475000,
        frequency: 'annual',
        direction: 'lower'
      },
      {
        id: 13,
        name: 'Landed Repairs Spend - Margin %',
        type: 'percent',
        value: 23.4,
        frequency: 'monthly',
        direction: 'higher'
      },
      {
        id: 14,
        name: 'Landed Repairs Spend - Margin $',
        type: 'dollar',
        value: 150000,
        frequency: 'monthly',
        direction: 'higher'
      },
      // Revenue/Margin
      {
        id: 15,
        name: 'Revenue/Margin - Monthly Goal Revenue',
        type: 'dollar',
        value: 550000,
        frequency: 'monthly',
        direction: 'higher'
      },
      {
        id: 16,
        name: 'Revenue/Margin - Margin %',
        type: 'percent',
        value: 23.4,
        frequency: 'monthly',
        direction: 'higher'
      },
      {
        id: 17,
        name: 'Revenue/Margin - Margin $',
        type: 'dollar',
        value: 150000,
        frequency: 'monthly',
        direction: 'higher'
      },
      // Inventory Health
      {
        id: 18,
        name: 'Inventory Health - 91-180 Days',
        type: 'percent',
        value: 26,
        frequency: 'monthly',
        direction: 'lower'
      },
      {
        id: 19,
        name: 'Inventory Health - 181-365 Days',
        type: 'percent',
        value: 16,
        frequency: 'monthly',
        direction: 'lower'
      },
      {
        id: 20,
        name: 'Inventory Health - 365+ Days',
        type: 'percent',
        value: 2,
        frequency: 'monthly',
        direction: 'lower'
      }
    ],
    sales: [],
    operations: []
  });

  const [editingValues, setEditingValues] = useState({});

  const getTypeIcon = (type) => {
    switch (type) {
      case 'dollar':
        return <IconCurrencyDollar size={16} />;
      case 'percent':
        return <IconPercentage size={16} />;
      case 'number':
        return <IconHash size={16} />;
      default:
        return null;
    }
  };

  const formatValue = (value, type) => {
    switch (type) {
      case 'dollar':
        return `$${value.toLocaleString()}`;
      case 'percent':
        return `${value}%`;
      case 'number':
        return value.toLocaleString();
      default:
        return value;
    }
  };

  const getPerformanceColor = () => {
    // Since we're only storing goal values, default to blue for display
    return 'blue';
  };

  const handleEdit = (metric) => {
    setEditingId(metric.id);
    setEditingValues({
      type: metric.type,
      value: metric.value,
      frequency: metric.frequency,
      direction: metric.direction
    });
  };

  const handleSave = (department, metricId) => {
    setMetrics({
      ...metrics,
      [department]: metrics[department].map(m =>
        m.id === metricId ? { ...m, ...editingValues } : m
      )
    });
    setEditingId(null);
    setEditingValues({});
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditingValues({});
  };

  const renderMetric = (metric, department) => {
    const isEditing = editingId === metric.id;
    const performanceColor = getPerformanceColor();

    if (isEditing) {
      return (
        <Paper key={metric.id} p="md" radius="sm" withBorder bg="blue.0">
          <Grid align="center" gutter="md">
            <Grid.Col span={4}>
              <Text c="dimmed" size="sm">{metric.name}</Text>
            </Grid.Col>
            <Grid.Col span={2}>
              <Select
                value={editingValues.type}
                onChange={(val) => setEditingValues({ ...editingValues, type: val })}
                data={[
                  { value: 'dollar', label: 'Dollar' },
                  { value: 'percent', label: 'Percent' },
                  { value: 'number', label: 'Number' }
                ]}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <NumberInput
                value={editingValues.value}
                onChange={(val) => setEditingValues({ ...editingValues, value: val })}
                precision={editingValues.type === 'percent' ? 1 : 0}
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <Select
                value={editingValues.frequency}
                onChange={(val) => setEditingValues({ ...editingValues, frequency: val })}
                data={[
                  { value: 'monthly', label: 'Monthly' },
                  { value: 'annual', label: 'Annual' }
                ]}
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <Select
                value={editingValues.direction}
                onChange={(val) => setEditingValues({ ...editingValues, direction: val })}
                data={[
                  { value: 'higher', label: '↑' },
                  { value: 'lower', label: '↓' }
                ]}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <Group gap="xs">
                <ActionIcon 
                  size="sm" 
                  color="green" 
                  variant="light"
                  onClick={() => handleSave(department, metric.id)}
                >
                  <IconCheck size={16} />
                </ActionIcon>
                <ActionIcon 
                  size="sm" 
                  color="red" 
                  variant="light"
                  onClick={handleCancel}
                >
                  <IconX size={16} />
                </ActionIcon>
              </Group>
            </Grid.Col>
          </Grid>
        </Paper>
      );
    }

    return (
      <Paper 
        key={metric.id} 
        p="md" 
        radius="sm" 
        withBorder
        style={{
          transition: 'all 0.2s ease',
          cursor: 'default'
        }}
      >
        <Grid align="center" gutter="md">
          <Grid.Col span={4}>
            <Text c="dimmed" size="sm">{metric.name}</Text>
          </Grid.Col>
          <Grid.Col span={2}>
            <Group gap="xs">
              <ThemeIcon size="sm" variant="light" color="gray">
                {getTypeIcon(metric.type)}
              </ThemeIcon>
              <Text c="dimmed" size="sm">{metric.type}</Text>
            </Group>
          </Grid.Col>
          <Grid.Col span={2}>
            <Badge 
              size="lg" 
              color={performanceColor} 
              variant="light"
              style={{ minWidth: '80px' }}
            >
              {formatValue(metric.value, metric.type)}
            </Badge>
          </Grid.Col>
          <Grid.Col span={1}>
            <Badge size="sm" color="gray" variant="light">
              {metric.frequency}
            </Badge>
          </Grid.Col>
         
          <Grid.Col span={2}>
            <ActionIcon 
              size="sm" 
              variant="light" 
              color="blue"
              onClick={() => handleEdit(metric)}
            >
              <IconEdit size={16} />
            </ActionIcon>
          </Grid.Col>
        </Grid>
      </Paper>
    );
  };

  return (
    <Container size="xl" p="md" mt="lg">
      <Title order={2} mb="xl">Scorecard Goals Configuration</Title>
      
      <Accordion
        defaultValue="repairs"
      >
        <Accordion.Item value="repairs">
          <Accordion.Control>
            <Group>
              <ThemeIcon size="md" radius="xl" variant="light" color="blue">
                <IconTargetArrow size={18} />
              </ThemeIcon>
              <Text fw={600}>Repairs Department Scorecard</Text>
              <Badge size="sm" color="gray" variant="light">
                {metrics.repairs.length} metrics
              </Badge>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            {/* Column Headers */}
            <Grid mb="md" px="md">
              <Grid.Col span={4}>
                <Text size="xs" c="dimmed" fw={600}>METRIC</Text>
              </Grid.Col>
              <Grid.Col span={2}>
                <Text size="xs" c="dimmed" fw={600}>TYPE</Text>
              </Grid.Col>
              <Grid.Col span={2}>
                <Text size="xs" c="dimmed" fw={600}>GOAL VALUE</Text>
              </Grid.Col>
              <Grid.Col span={1}>
                <Text size="xs" c="dimmed" fw={600}>FREQ</Text>
              </Grid.Col>
             
              <Grid.Col span={2}>
                <Text size="xs" c="dimmed" fw={600}>ACTION</Text>
              </Grid.Col>
            </Grid>
            
            <Stack gap="sm">
              {metrics.repairs.map(metric => renderMetric(metric, 'repairs'))}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="repairsIndividual">
          <Accordion.Control>
            <Group>
              <ThemeIcon size="md" radius="xl" variant="light" color="violet">
                <IconTargetArrow size={18} />
              </ThemeIcon>
              <Text fw={600}>Repairs Individual Scorecard</Text>
              <Badge size="sm" color="gray" variant="light">
                {metrics.repairsIndividual.length} metrics
              </Badge>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            {/* Column Headers */}
            <Grid mb="md" px="md">
              <Grid.Col span={4}>
                <Text size="xs" c="dimmed" fw={600}>METRIC</Text>
              </Grid.Col>
              <Grid.Col span={2}>
                <Text size="xs" c="dimmed" fw={600}>TYPE</Text>
              </Grid.Col>
              <Grid.Col span={2}>
                <Text size="xs" c="dimmed" fw={600}>GOAL VALUE</Text>
              </Grid.Col>
              <Grid.Col span={1}>
                <Text size="xs" c="dimmed" fw={600}>FREQ</Text>
              </Grid.Col>
             
              <Grid.Col span={2}>
                <Text size="xs" c="dimmed" fw={600}>ACTION</Text>
              </Grid.Col>
            </Grid>
            
            <Stack gap="sm">
              {metrics.repairsIndividual.map(metric => renderMetric(metric, 'repairsIndividual'))}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="sales">
          <Accordion.Control>
            <Group>
              <ThemeIcon size="md" radius="xl" variant="light" color="green">
                <IconCurrencyDollar size={18} />
              </ThemeIcon>
              <Text fw={600}>Sales Department Scorecard</Text>
              <Badge size="sm" color="gray" variant="light">
                {metrics.sales.length} metrics
              </Badge>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Text c="dimmed" ta="center" py="xl">
              No metrics configured
            </Text>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="operations">
          <Accordion.Control>
            <Group>
              <ThemeIcon size="md" radius="xl" variant="light" color="orange">
                <IconTargetArrow size={18} />
              </ThemeIcon>
              <Text fw={600}>Purchasing Department Scorecard</Text>
              <Badge size="sm" color="gray" variant="light">
                {metrics.operations.length} metrics
              </Badge>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Text c="dimmed" ta="center" py="xl">
              No metrics configured
            </Text>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Container>
  );
};

export default GoalManagement;